import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import {
  ArrowLeft, Star, ShieldCheck, HelpCircle, Heart, Award, Check,
  Store, Info, Loader2,
} from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';
import BottomNavigation from '../components/BottomNavigation';

const SectionCard = ({ children, className = '', dark = false, ...rest }) => (
  <section className={`${dark ? 'bg-slate-800' : 'bg-white'} rounded-2xl shadow-md p-5 sm:p-6 ${className}`} {...rest}>{children}</section>
);

const money = (amount, currency, interval, t) => {
  if (!amount || amount === 0) return t('bg.free', 'Free');
  const sym = (currency || 'eur').toLowerCase() === 'eur' ? '€' : '';
  const per = interval === 'year'
    ? t('bg.perYear', '/year')
    : interval === 'month'
      ? t('bg.perMonth', '/month')
      : '';
  return `${sym}${amount.toFixed(2)}${per}`;
};

export default function BusinessGuide() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axiosInstance.get('/membership/tiers')
      .then((res) => { if (active) setTiers(res.data?.tiers || []); })
      .catch(() => { if (active) setTiers([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const freeTier = tiers.find((x) => x.id === 'free');
  const paidTiers = tiers.filter((x) => x.id !== 'free');

  const badges = [
    {
      key: 'verified',
      icon: ShieldCheck,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      title: t('verifiedOwner', 'Verified Business Owner'),
      desc: t('bg.badgeVerifiedDesc', 'The business owner has created or claimed this listing and confirmed their details. You are seeing information managed directly by the business.'),
    },
    {
      key: 'unclaimed',
      icon: Info,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
      title: t('unclaimedListing', 'Unclaimed Listing'),
      desc: t('bg.badgeUnclaimedDesc', 'This listing was added to help the community find the business, but no owner has claimed it yet. Some contact details may be limited until the owner verifies it.'),
    },
    {
      key: 'charity',
      icon: Heart,
      color: 'text-teal-600 bg-teal-50 border-teal-200',
      title: t('charity.charityBadge', 'Community & Charity'),
      desc: t('bg.badgeCharityDesc', 'A recognised community group, non-profit or charity. This badge highlights organisations that serve the local community.'),
    },
    {
      key: 'founding',
      icon: Award,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      title: t('foundingBadge.label', 'Founding Business'),
      desc: t('bg.badgeFoundingDesc', 'One of the early businesses that helped launch Find-2-Day. A recognition badge only — it does not affect customer ratings.'),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="business-guide-page">
      {/* HEADER */}
      <header className="bg-gradient-to-b from-[#132449] to-[#1e2f5c] text-white px-4 pt-5 pb-7 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/90 hover:text-white bg-white/10 rounded-full px-3 py-1.5"
            data-testid="bg-back-button"
            aria-label={t('back', 'Back')}
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-semibold">{t('back', 'Back')}</span>
          </button>
          <LanguageToggle />
        </div>
        <div className="mt-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-[12px] font-semibold">
            <HelpCircle className="h-4 w-4" />
            {t('bg.eyebrow', 'For visitors & businesses')}
          </div>
          <h1 className="mt-3 text-2xl sm:text-3xl font-black leading-tight" data-testid="bg-title">
            {t('bg.title', 'Business Memberships, Ratings & Trust Badges — Explained')}
          </h1>
          <p className="mt-2 text-white/80 text-sm leading-relaxed">
            {t('bg.intro', 'A quick guide to how businesses appear on Find-2-Day: the membership options they can choose, the star ratings left by real customers, and the badges that show a listing\u2019s status. Understand these before choosing a business or a membership.')}
          </p>
        </div>
      </header>

      <main className="px-3 sm:px-4 -mt-3 space-y-4 max-w-3xl mx-auto">
        {/* MEMBERSHIP PLANS */}
        <SectionCard className="mt-4" data-testid="bg-memberships">
          <div className="flex items-center gap-2 mb-1">
            <Store className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-black text-slate-800">{t('bg.plansTitle', 'Business Membership Options')}</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            {t('bg.plansIntro', 'Every business can list for free. Paid memberships simply unlock more of the profile — photos, contact details, events and offers. They do not change a business\u2019s star rating.')}
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('bg.loading', 'Loading plans\u2026')}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Free Directory */}
              {freeTier && (
                <div className="rounded-xl border border-slate-200 p-4" data-testid="bg-plan-free">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-extrabold text-slate-800">{freeTier.displayName || 'Free Directory'}</h3>
                    <span className="text-emerald-600 font-black">{money(freeTier.amount, freeTier.currency, freeTier.interval, t)}</span>
                  </div>
                  <p className="text-[12px] text-slate-500 mt-0.5">{t('bg.freeTag', 'No card required · stays free')}</p>
                  <ul className="mt-2 space-y-1">
                    {(freeTier.publicFeatures || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Paid tiers */}
              {paidTiers.map((tier) => (
                <div key={tier.id} className="rounded-xl border border-purple-200 bg-purple-50/40 p-4" data-testid={`bg-plan-${tier.id}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-extrabold text-slate-800">{tier.displayName}</h3>
                    <span className="text-purple-700 font-black">{money(tier.amount, tier.currency, tier.interval, t)}</span>
                  </div>
                  {tier.trialDays > 0 && (
                    <span className="inline-block mt-1 text-[11px] font-bold text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">
                      {t('bg.trialBadge', 'First {{days}} days free', { days: tier.trialDays })}
                    </span>
                  )}
                  <ul className="mt-2 space-y-1">
                    {(tier.publicFeatures || [])
                      .filter((f) => !(tier.trialDays > 0 && /first.*days free/i.test(f)))
                      .map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-purple-500 shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Trial + founding notes */}
          <div className="mt-4 rounded-xl bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900" data-testid="bg-trial-note">
            <p className="font-semibold">{t('bg.trialNoteTitle', 'New paid memberships: 60-day free trial')}</p>
            <p className="mt-1 text-sky-800">{t('bg.trialNoteBody', 'New paid business subscriptions start with a 60-day free trial. You are not charged during the trial and can cancel any time before it ends.')}</p>
          </div>
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="bg-founding-note">
            <p className="font-semibold">{t('bg.foundingNoteTitle', 'Founding businesses: 12 months free')}</p>
            <p className="mt-1 text-amber-800">{t('bg.foundingNoteBody', 'Existing founding businesses keep their 12 months free under the Founding Business Offer — this is unchanged.')}</p>
          </div>
        </SectionCard>

        {/* Register your business CTA (existing signup flow) */}
        <button
          onClick={() => navigate('/business/signup')}
          data-testid="bg-register-business-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#132449] to-[#1e2f5c] text-white font-extrabold px-5 py-4 shadow-md active:scale-[0.99] transition-transform"
        >
          <Store className="h-5 w-5 text-amber-300" />
          {t('bg.registerCta', 'Register Your Business \u2013 Start Free')}
        </button>

        {/* STAR RATINGS */}
        <SectionCard data-testid="bg-ratings">
          <div className="flex items-center gap-2 mb-1">
            <Star className="h-5 w-5 text-amber-500 fill-amber-400" />
            <h2 className="text-lg font-black text-slate-800">{t('bg.ratingsTitle', 'Customer Star Ratings (1\u20135)')}</h2>
          </div>
          <div className="flex items-center gap-1 my-2" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="h-6 w-6 text-amber-400 fill-amber-400" />
            ))}
          </div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{t('bg.rating1', 'Ratings come only from people in the community who leave a review — never from the business itself.')}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{t('bg.rating2', 'Each review gives a score from 1 to 5 stars along with a short comment.')}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{t('bg.rating3', 'Reviews are checked by moderators before they appear, to keep things fair and genuine.')}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{t('bg.rating4', 'A business\u2019s overall rating is simply the average of its approved reviews, shown with the number of reviews.')}</li>
          </ul>
          <button
            onClick={() => navigate('/review-guidelines')}
            className="mt-4 text-sm font-semibold text-purple-700 hover:text-purple-900 underline"
            data-testid="bg-review-guidelines-link"
          >
            {t('bg.reviewGuidelinesLink', 'Read our review guidelines \u2192')}
          </button>
        </SectionCard>

        {/* TRUST BADGES */}
        <SectionCard data-testid="bg-badges">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-black text-slate-800">{t('bg.badgesTitle', 'Trust & Status Badges')}</h2>
          </div>
          <div className="space-y-3">
            {badges.map((b) => (
              <div key={b.key} className="flex items-start gap-3" data-testid={`bg-badge-${b.key}`}>
                <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full border ${b.color} shrink-0`}>
                  <b.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-bold text-slate-800 text-sm">{b.title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* FAIRNESS NOTE */}
        <SectionCard dark className="text-white" data-testid="bg-fairness">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-sky-300 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-black text-base">{t('bg.fairTitle', 'Ratings and trust can\u2019t be bought')}</h2>
              <p className="mt-1 text-sm text-slate-200 leading-relaxed">
                {t('bg.fairBody', 'Paying for a membership never improves a business\u2019s star rating and does not guarantee trust. Star ratings always come from genuine customer reviews, and badges only show a listing\u2019s status — not its quality.')}
              </p>
            </div>
          </div>
        </SectionCard>
      </main>

      <BottomNavigation />
    </div>
  );
}
